import {
  buildCaptionCues,
  getCaptionWordsFromSegments,
  toCaptionCueLayout,
  wordsFromCaptionSegment,
} from '~/components/AssetModalV2/Content/Panel/Captions/buildCaptionCues';
import {
  type CaptionHoldMode,
  type CueLayout,
  DEFAULT_CUE_LAYOUT,
} from '~/components/AssetModalV2/Content/Panel/Captions/captionStyleTypes';
import { type CaptionCueLayout } from '~/components/AssetModalV2/Content/Panel/Captions/getCaptionCueLayout';
import {
  type TranscriptItemType,
  type TranscriptSegmentType,
} from '~/components/AssetModalV2/Content/Panel/Transcript/types';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type ClipTranscriptCache, type TimeRange } from '~/swr-hooks/clips/transcript/types';

/** Transcript fetch window centered on playback time, matching the transcript panel. */
export const getTranscriptFetchRangeForTimeMs = (currentTimeMs: number): TimeRange => {
  const startTimeMs = Math.max(0, currentTimeMs - MAX_TRANSCRIPT_RANGE_IN_MS / 2);
  const endTimeMs = startTimeMs === 0 ? MAX_TRANSCRIPT_RANGE_IN_MS : currentTimeMs + MAX_TRANSCRIPT_RANGE_IN_MS / 2;

  return { startTimeMs, endTimeMs };
};

export const isTranscriptFetchRangeCoveringTime = (currentTimeMs: number, range: TimeRange | null): boolean =>
  range !== null && currentTimeMs >= range.startTimeMs && currentTimeMs <= range.endTimeMs;

export type VideoContentBounds = {
  height: number;
  left: number;
  top: number;
  width: number;
};

export type GetVideoContentBoundsParams = {
  containerHeight: number;
  containerWidth: number;
  videoHeight: number;
  videoWidth: number;
};

/** Excludes letterbox/pillarbox bars so captions align and scale to the rendered video content. */
export const getVideoContentBounds = ({
  containerHeight,
  containerWidth,
  videoHeight,
  videoWidth,
}: GetVideoContentBoundsParams): VideoContentBounds | null => {
  if (containerHeight <= 0 || containerWidth <= 0 || videoHeight <= 0 || videoWidth <= 0) {
    return null;
  }

  const videoAspectRatio = videoWidth / videoHeight;
  const containerAspectRatio = containerWidth / containerHeight;
  const width = videoAspectRatio > containerAspectRatio ? containerWidth : containerHeight * videoAspectRatio;
  const height = videoAspectRatio > containerAspectRatio ? containerWidth / videoAspectRatio : containerHeight;

  return {
    height,
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
  };
};

export const findCurrentCaptionSegment = (
  cache: ClipTranscriptCache,
  currentTimeMs: number,
): TranscriptSegmentType | null => {
  for (const interval of cache.data) {
    if (currentTimeMs < interval.startTimeMs || currentTimeMs > interval.endTimeMs) {
      continue;
    }

    for (const segment of interval.data) {
      if (currentTimeMs >= segment.startTimeMs && currentTimeMs <= segment.endTimeMs) {
        return segment;
      }
    }
  }

  return null;
};

export const isTimeCoveredByTranscriptCache = (cache: ClipTranscriptCache, currentTimeMs: number): boolean =>
  cache.data.some((interval) => currentTimeMs >= interval.startTimeMs && currentTimeMs <= interval.endTimeMs);

export type CaptionResolveResult =
  | { status: 'segment'; segment: TranscriptSegmentType; displayTimeMs: number }
  | { status: 'interval-loading' }
  | { status: 'hidden' };

/** Either nothing is shown, or a resolved cue is shown. Fetching is signalled separately. */
export type CaptionVisualState =
  | { status: 'hidden' }
  | {
      activeWordIndexes: number[];
      cue: CaptionCueLayout;
      status: 'segment';
      /** Flattened pronunciation words used for cue layout + rendering (burn-parity across segments). */
      words: TranscriptItemType[];
    };

export type CaptionHighlightOptions = {
  enabled: boolean;
  holdGraceMs?: number;
  holdMode?: CaptionHoldMode;
  leadMs?: number;
};

export type ResolveCaptionVisualStateParams = {
  cache: ClipTranscriptCache;
  cueConstraints?: CueLayout;
  currentTimeMs: number;
  highlight: CaptionHighlightOptions;
};

export type ResolveCaptionVisualStateResult = {
  /** True when the current time falls outside the loaded transcript and a range fetch is needed. */
  shouldFetchRange: boolean;
  state: CaptionVisualState;
};

/**
 * Transcript segments from the loaded cache window. Cue packing respects segment
 * boundaries the same way backend `buildCues` does on the full transcript.
 */
export const getCaptionSegmentsFromCache = (cache: ClipTranscriptCache): TranscriptSegmentType[] =>
  cache.data.flatMap((interval) => interval.data);

/**
 * Flattened timed words from loaded segments for highlight timing and rendering.
 * Trailing punctuation attaches to the preceding pronunciation; leading punctuation
 * is kept as its own word.
 */
export const getPronunciationItemsFromCache = (cache: ClipTranscriptCache): TranscriptItemType[] => {
  const words: TranscriptItemType[] = [];
  const seen = new Set<string>();

  for (const segment of getCaptionSegmentsFromCache(cache)) {
    for (const word of wordsFromCaptionSegment(segment)) {
      const key = `${word.startTimeMs}:${word.endTimeMs}:${word.text}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      words.push({
        endTimeMs: word.endTimeMs,
        startTimeMs: word.startTimeMs,
        text: word.text,
        type: 'pronunciation',
      });
    }
  }

  words.sort((left, right) => left.startTimeMs - right.startTimeMs || left.endTimeMs - right.endTimeMs);

  return words;
};

/**
 * Cues never cross segment boundaries, so each playback tick only needs the
 * segment under the playhead (plus the next for end-time clamping). During
 * holdGrace after a segment ends, fall back to that prior segment.
 */
const getCaptionSegmentsForActiveCue = (
  segments: TranscriptSegmentType[],
  currentTimeMs: number,
  holdGraceMs: number,
): TranscriptSegmentType[] => {
  const containingIndex = segments.findIndex(
    (segment) => currentTimeMs >= segment.startTimeMs && currentTimeMs <= segment.endTimeMs,
  );

  if (containingIndex !== -1) {
    return segments.slice(containingIndex, containingIndex + 2);
  }

  if (holdGraceMs <= 0) {
    return [];
  }

  for (let index = segments.length - 1; index >= 0; index--) {
    const segment = segments[index];

    if (segment && segment.endTimeMs < currentTimeMs && currentTimeMs <= segment.endTimeMs + holdGraceMs) {
      return [segment];
    }
  }

  return [];
};

const findActiveCaptionCue = ({
  cueConstraints,
  currentTimeMs,
  holdGraceMs,
  segments,
}: {
  cueConstraints: CueLayout;
  currentTimeMs: number;
  holdGraceMs: number;
  segments: TranscriptSegmentType[];
}) => {
  const segmentsForCue = getCaptionSegmentsForActiveCue(segments, currentTimeMs, holdGraceMs);

  if (segmentsForCue.length === 0) {
    return undefined;
  }

  const cues = buildCaptionCues({ layout: cueConstraints, segments: segmentsForCue });

  return cues.find((cue) => currentTimeMs >= cue.startTimeMs && currentTimeMs <= cue.endTimeMs + holdGraceMs);
};

/**
 * Mirrors backend ASS karaoke highlight timing:
 * - `leadMs` advances when a word becomes active
 * - `holdGraceMs` extends the last active word after its end
 * - `gapless` keeps the previous word lit until the next word's lead start (no dark gaps)
 * - `hybrid` only lights a word inside its [start−lead, end+grace] window
 *
 * At most one word is active (burn paints one highlight color at a time).
 */
export const getActiveCaptionWordIndexes = ({
  currentTimeMs,
  highlight,
  words,
}: {
  currentTimeMs: number;
  highlight: CaptionHighlightOptions;
  words: TranscriptItemType[];
}): number[] => {
  if (!highlight.enabled || words.length === 0) {
    return [];
  }

  const leadMs = highlight.leadMs ?? 0;
  const holdGraceMs = highlight.holdGraceMs ?? 0;
  const holdMode = highlight.holdMode ?? 'gapless';
  const leadTimeMs = currentTimeMs + leadMs;

  if (holdMode === 'hybrid') {
    let activeIndex = -1;

    for (let index = 0; index < words.length; index++) {
      const word = words[index];

      if (!word) {
        continue;
      }

      if (leadTimeMs >= word.startTimeMs && currentTimeMs <= word.endTimeMs + holdGraceMs) {
        activeIndex = index;
      }
    }

    return activeIndex === -1 ? [] : [activeIndex];
  }

  let activeIndex = -1;

  for (let index = 0; index < words.length; index++) {
    const word = words[index];

    if (!word) {
      continue;
    }

    if (leadTimeMs >= word.startTimeMs) {
      activeIndex = index;
    } else {
      break;
    }
  }

  if (activeIndex === -1) {
    return [];
  }

  const activeWord = words[activeIndex];
  const nextWord = words[activeIndex + 1];

  if (!activeWord) {
    return [];
  }

  // After the final word, drop the highlight once grace expires (burn event ends).
  if (!nextWord && currentTimeMs > activeWord.endTimeMs + holdGraceMs) {
    return [];
  }

  return [activeIndex];
};

/**
 * Resolves which segment (if any) owns the playhead. Kept for callers that need the
 * raw AWS segment; caption visibility uses cue time spans instead (see
 * `resolveCaptionVisualState`) so short inter-segment gaps inside a cue stay visible.
 */
export const resolveCaptionSegment = (cache: ClipTranscriptCache, currentTimeMs: number): CaptionResolveResult => {
  const currentSegment = findCurrentCaptionSegment(cache, currentTimeMs);

  if (currentSegment) {
    return {
      status: 'segment',
      segment: currentSegment,
      displayTimeMs: currentTimeMs,
    };
  }

  if (!isTimeCoveredByTranscriptCache(cache, currentTimeMs)) {
    return { status: 'interval-loading' };
  }

  return { status: 'hidden' };
};

export const resolveCaptionVisualState = ({
  cache,
  cueConstraints = DEFAULT_CUE_LAYOUT,
  currentTimeMs,
  highlight,
}: ResolveCaptionVisualStateParams): ResolveCaptionVisualStateResult => {
  if (!isTimeCoveredByTranscriptCache(cache, currentTimeMs)) {
    return {
      shouldFetchRange: true,
      state: { status: 'hidden' },
    };
  }

  // Pack cues from loaded segments only; segment boundaries match the burn pipeline.
  const segments = getCaptionSegmentsFromCache(cache);
  const timedWords = getCaptionWordsFromSegments(segments);

  if (timedWords.length === 0) {
    return {
      shouldFetchRange: false,
      state: { status: 'hidden' },
    };
  }

  const holdGraceMs = highlight.enabled ? (highlight.holdGraceMs ?? 0) : 0;
  const activeCue = findActiveCaptionCue({
    cueConstraints,
    currentTimeMs,
    holdGraceMs,
    segments,
  });

  if (!activeCue) {
    return {
      shouldFetchRange: false,
      state: { status: 'hidden' },
    };
  }

  const cue = toCaptionCueLayout({ cue: activeCue, words: timedWords });
  const words = timedWords.map(
    (word) =>
      ({
        endTimeMs: word.endTimeMs,
        startTimeMs: word.startTimeMs,
        text: word.text,
        type: 'pronunciation',
      }) satisfies TranscriptItemType,
  );

  const activeWordIndexes = getActiveCaptionWordIndexes({
    currentTimeMs,
    highlight,
    words: words.slice(cue.startIndex, cue.endIndex),
  }).map((index) => cue.startIndex + index);

  return {
    shouldFetchRange: false,
    state: {
      activeWordIndexes,
      cue,
      status: 'segment',
      words,
    },
  };
};

/**
 * Runs on every playback tick, so it compares only what the overlay renders:
 * the visible cue layout, which words are highlighted, and every word in the
 * active cue (so mid-cue transcript edits invalidate the cached visual state).
 */
export const areCaptionVisualStatesEqual = (first: CaptionVisualState, second: CaptionVisualState): boolean => {
  if (first.status !== second.status) {
    return false;
  }

  if (first.status !== 'segment' || second.status !== 'segment') {
    return true;
  }

  if (
    first.words.length !== second.words.length ||
    first.cue.endIndex !== second.cue.endIndex ||
    first.cue.startIndex !== second.cue.startIndex ||
    first.cue.lines.length !== second.cue.lines.length ||
    first.activeWordIndexes.length !== second.activeWordIndexes.length
  ) {
    return false;
  }

  if (
    !first.cue.lines.every(
      (line, index) =>
        line.startIndex === second.cue.lines[index]?.startIndex && line.endIndex === second.cue.lines[index]?.endIndex,
    )
  ) {
    return false;
  }

  if (!first.activeWordIndexes.every((wordIndex, index) => wordIndex === second.activeWordIndexes[index])) {
    return false;
  }

  for (let index = first.cue.startIndex; index < first.cue.endIndex; index++) {
    const left = first.words[index];
    const right = second.words[index];

    if (
      left === undefined ||
      right === undefined ||
      left.startTimeMs !== right.startTimeMs ||
      left.endTimeMs !== right.endTimeMs ||
      left.text !== right.text
    ) {
      return false;
    }
  }

  return true;
};
